import { alpha } from "@mui/material/styles";

/**
 * Shared `sx` colour callbacks for the tinted surfaces that recur across the
 * app: the soft green icon tiles, the red danger panels, the amber unsaved
 * banners and the inset console panes.
 *
 * Each one reads its shade off the active palette rather than baking in a
 * literal, so a tint that is a whisper of green on white stays a whisper of
 * green on the dark surface instead of vanishing into it. Use them anywhere the
 * same tint appears more than once, and reach for `alpha` directly for one-offs.
 */

export const primaryTint = (theme) => alpha(theme.palette.primary.main, 0.11);

export const primaryTintBorder = (theme) =>
    alpha(theme.palette.primary.main, 0.18);

export const primarySurface = (theme) =>
    alpha(theme.palette.primary.main, 0.07);

export const primaryHoverBorder = (theme) =>
    alpha(theme.palette.primary.main, 0.4);

export const primaryFocusRing = (theme) =>
    `3px solid ${alpha(theme.palette.primary.main, 0.3)}`;

export const errorTint = (theme) => alpha(theme.palette.error.main, 0.09);

export const errorTintBorder = (theme) =>
    alpha(theme.palette.error.main, 0.28);

export const errorHoverTint = (theme) => alpha(theme.palette.error.main, 0.16);

export const warningBorder = (theme) =>
    alpha(theme.palette.warning.main, 0.35);

export const warningSurface = (theme) =>
    theme.palette.mode === "dark"
        ? alpha(theme.palette.warning.main, 0.14)
        : "#fff8ef";

/**
 * A pane inset into a card — a log console, a scrolling picker. It should read
 * a shade back from the paper it sits on, which means going lighter on white
 * and darker on the dark surface.
 */
export const insetSurface = (theme) =>
    theme.palette.mode === "dark"
        ? theme.palette.background.default
        : "#fbfcfb";

/** Spread into an `sx` callback's result to get a palette-aware thin scrollbar. */
export const thinScrollbar = (theme) => ({
    scrollbarWidth: "thin",
    scrollbarColor: `${alpha(theme.palette.text.primary, 0.28)} transparent`,
    "&::-webkit-scrollbar": { width: 7, height: 7 },
    "&::-webkit-scrollbar-thumb": {
        backgroundColor: alpha(theme.palette.text.primary, 0.22),
        borderRadius: 999,
    },
});
